// src/pages/RichiestaFeriePage.js
import React, { useEffect, useState } from "react";
import { collection, query, where, onSnapshot, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { messaggioErrore } from "../utils/erroriUtente";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { oggiStr } from "../utils/dateUtils";
import { CalendarDays, Send } from "lucide-react";
import { validaRichiesta, descriviRichiesta, minutiTra, testoDurata } from "../utils/feriePermessi";

const STATI = {
  in_attesa: { label:"In attesa",  bg:"#dbeafe", col:"#1d4ed8" },
  approvata: { label:"Approvata",  bg:"#d1fae5", col:"#065f46" },
  rifiutata: { label:"Rifiutata",  bg:"#fee2e2", col:"#991b1b" },
};

export default function RichiestaFeriePage() {
  const { currentUser } = useAuth();
  const { effectiveOrgId: orgId, orgVisitata } = useOrg();
  const oreGiornata = Number(orgVisitata?.oreLavorativeStandard) || 8;
  const { addToast } = useToast();

  const [tipo, setTipo] = useState("ferie");
  const [dataInizio, setDataInizio] = useState(oggiStr());
  const [dataFine, setDataFine] = useState(oggiStr());
  // Permesso: giornata intera (dal … al …) oppure a ore (un giorno, dalle … alle …)
  const [aOre, setAOre] = useState(false);
  const [oraInizio, setOraInizio] = useState("10:00");
  const [oraFine, setOraFine] = useState("12:00");
  const permessoAOre = tipo === "permesso" && aOre;
  const [note, setNote] = useState("");
  const [inviando, setInviando] = useState(false);
  const [mieRichieste, setMieRichieste] = useState([]);

  useEffect(() => {
    if (!orgId || !currentUser) return;
    return onSnapshot(
      query(collection(db,"richiesteFerie"), where("organizationId","==",orgId), where("uid","==",currentUser.uid)),
      snap => {
        const l = snap.docs.map(d => ({ id:d.id, ...d.data() }));
        l.sort((a,b) => (b.createdAt?.toMillis?.()||0) - (a.createdAt?.toMillis?.()||0));
        setMieRichieste(l);
      }
    );
  }, [orgId, currentUser]);

  const invia = async (e) => {
    e.preventDefault();
    const errore = validaRichiesta({ tipo, aOre: permessoAOre, dataInizio, dataFine, oraInizio, oraFine }, oreGiornata);
    if (errore) { addToast(errore,"error"); return; }
    setInviando(true);
    try {
      await addDoc(collection(db,"richiesteFerie"), {
        organizationId: orgId,
        uid: currentUser.uid,
        displayName: currentUser.displayName || "",
        tipo,
        dataInizio,
        dataFine: permessoAOre ? dataInizio : dataFine,
        ...(permessoAOre ? { aOre: true, oraInizio, oraFine, minuti: minutiTra(oraInizio, oraFine) } : {}),
        note: note.trim(),
        stato: "in_attesa",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      addToast("Richiesta inviata ✅ — in attesa di approvazione","success");
      setNote("");
    } catch(e) {
      addToast(messaggioErrore(e), "error");
    }
    setInviando(false);
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      <div style={{ background:"linear-gradient(135deg,#1e1b4b,#312e81)", padding:"16px 24px", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <CalendarDays size={20} color="#c4b5fd"/>
          <h2 style={{ fontSize:18, fontWeight:800, color:"#fff", margin:0 }}>Ferie e Permessi</h2>
        </div>
      </div>

      <div style={{ flex:1, overflowY:"auto", padding:24, background:"#f9fafb" }}>
        <form onSubmit={invia} style={{ background:"#fff", borderRadius:12, border:"1px solid #e5e7eb", padding:20, maxWidth:480, marginBottom:24 }}>
          <h3 style={{ fontSize:15, fontWeight:700, marginBottom:14 }}>Nuova richiesta</h3>

          <div className="form-group">
            <label className="form-label">Tipo</label>
            <div style={{ display:"flex", gap:8 }}>
              {[{v:"ferie",l:"Ferie"},{v:"permesso",l:"Permesso"}].map(o => (
                <button type="button" key={o.v} onClick={() => setTipo(o.v)}
                  style={{ flex:1, padding:"9px", border:`2px solid ${tipo===o.v?"#7c3aed":"#e5e7eb"}`, borderRadius:9,
                    background: tipo===o.v?"#f5f3ff":"#fff", color: tipo===o.v?"#7c3aed":"#374151",
                    fontWeight:600, fontSize:13, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          {tipo === "permesso" && (
            <div className="form-group">
              <label className="form-label">Durata</label>
              <div style={{ display:"flex", gap:8 }}>
                {[{v:false,l:"Giornata intera"},{v:true,l:"A ore"}].map(o => (
                  <button type="button" key={o.l} onClick={() => setAOre(o.v)}
                    style={{ flex:1, padding:"8px", border:`2px solid ${aOre===o.v?"#7c3aed":"#e5e7eb"}`, borderRadius:9,
                      background: aOre===o.v?"#f5f3ff":"#fff", color: aOre===o.v?"#7c3aed":"#374151",
                      fontWeight:600, fontSize:12.5, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
          )}

          {permessoAOre ? (
            <>
              <div className="form-group">
                <label className="form-label">Giorno</label>
                <input className="input" type="date" value={dataInizio} onChange={e=>setDataInizio(e.target.value)} required/>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div className="form-group">
                  <label className="form-label">Dalle</label>
                  <input className="input" type="time" step={900} value={oraInizio} onChange={e=>setOraInizio(e.target.value)} required/>
                </div>
                <div className="form-group">
                  <label className="form-label">Alle</label>
                  <input className="input" type="time" step={900} value={oraFine} onChange={e=>setOraFine(e.target.value)} required/>
                </div>
              </div>
              <div style={{ fontSize:12.5, fontWeight:700, color: minutiTra(oraInizio, oraFine) ? "#5b21b6" : "#dc2626", marginTop:-4, marginBottom:12 }}>
                {minutiTra(oraInizio, oraFine) ? `Permesso di ${testoDurata(minutiTra(oraInizio, oraFine))}` : "L'ora di fine deve essere dopo l'ora di inizio"}
              </div>
            </>
          ) : (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <div className="form-group">
                <label className="form-label">Dal</label>
                <input className="input" type="date" value={dataInizio} onChange={e=>setDataInizio(e.target.value)} required/>
              </div>
              <div className="form-group">
                <label className="form-label">Al</label>
                <input className="input" type="date" value={dataFine} onChange={e=>setDataFine(e.target.value)} required/>
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Nota (opzionale)</label>
            <textarea className="input" rows={2} value={note} onChange={e=>setNote(e.target.value)} placeholder="Es. motivo, o altre indicazioni per chi approva"/>
          </div>

          <div style={{ fontSize:11.5, color:"#9ca3af", marginBottom:14 }}>
            {permessoAOre
              ? "Una volta approvato, il permesso compare da solo nel tuo cartellino in quell'orario e le sue ore si tolgono da quelle lavorate. Quel giorno timbra come sempre."
              : `Una volta approvata, i giorni compariranno automaticamente nel tuo cartellino come ${tipo==="ferie"?"Ferie":"Permesso"} (giornata intera).`}
          </div>

          <button type="submit" disabled={inviando}
            style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:6,
              padding:"11px", background:"#7c3aed", color:"#fff", border:"none", borderRadius:9,
              fontSize:14, fontWeight:600, cursor:"pointer", fontFamily:"'Inter',sans-serif", opacity:inviando?0.7:1 }}>
            <Send size={14}/> {inviando?"Invio...":"Invia richiesta"}
          </button>
        </form>

        <h3 style={{ fontSize:14, fontWeight:700, color:"#374151", marginBottom:10 }}>Le tue richieste</h3>
        {mieRichieste.length === 0 ? (
          <div style={{ color:"#9ca3af", fontSize:13 }}>Nessuna richiesta ancora inviata</div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:8, maxWidth:560 }}>
            {mieRichieste.map(r => {
              const s = STATI[r.stato] || STATI.in_attesa;
              return (
                <div key={r.id} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:"10px 14px",
                  display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap" }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:"#111827" }}>
                      {descriviRichiesta(r)}
                    </div>
                    {r.note && <div style={{ fontSize:11.5, color:"#6b7280", marginTop:2 }}>{r.note}</div>}
                  </div>
                  <span style={{ fontSize:11, fontWeight:700, color:s.col, background:s.bg, borderRadius:20, padding:"3px 10px", whiteSpace:"nowrap" }}>
                    {s.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
